import { t } from 'i18next';
import { Component, ReactElement } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from '../../../../../../shared/common/types/common';
import { TRaidSummary } from '../../../../shared/types/raidTypes';
import { createActionSetError, TError } from '../../../../../../shared/frontend/scripts/actions/errorActions';
import { createActionGetGuildDetails } from '../../actions/getGuildDetails';
import { createActionSetGuildPageLoading } from '../../actions/setGuildPageLoading';
import GuildActionCreator from '../../actions/guildActionCreator';
import EncounterDetailsContent from '../../components/container/encounterDetailsTooltipContent';
import { Spinner } from '../../../../../../shared/frontend/components/presentational/spinner';
import frontendLog from '../../libs/frontendLog';
import history from '../../../../../../shared/frontend/libs/history';
import { TGuildDetailsClientState, TGuildDetailsServerState } from '../../libs/store';
import { areFieldsDifferent } from '../../../../../../shared/frontend/libs/utils';
import apiService, { apiServiceAsync, TGetGuildDetailsParams } from '../../services/apiService';
import SkeletonGuildRaidCompositionPage from '../../../../../../shared/frontend/components/guild/SkeletonGuildRaidCompositionPage';

type TPropsFromState = TGuildDetailsClientState & {
  isFullProfile: boolean;
  raids: TRaidSummary[];
};

type TPropsThatDispatch = {
  setGuildDetails: (actionData: TGuildDetailsClientState) => void;
  setGuildPageLoading: (loading: boolean) => void;
  showError: (actionData: TError) => void;
};

type TOwnProps = RouteComponentProps<any, any>;

export type TGuildDetailsProps = TPropsFromState & TPropsThatDispatch & TOwnProps;

function isExpectedMissingBossDetailsError(err: any) {
  const responseStatus = err?.response?.status ?? err?.response?.statusCode ?? err?.status ?? err?.statusCode;
  const messageStatus = typeof err?.message === 'string' ? err.message.match(/status(?: code)?\s+(\d{3})/i)?.[1] : null;
  const statusCode = Number(responseStatus ?? messageStatus);

  return statusCode === 403 || statusCode === 422;
}

function GuildDetailsContent(props: any) {
  if (!props.guild) {
    return <SkeletonGuildRaidCompositionPage />;
  }

  return (
    <div>
      <EncounterDetailsContent
        withHash={true}
        hidePermalink={true}
        difficulty={props.match.params.difficulty}
        raid={{
          slug: props.match.params.raid
        }}
        boss={{
          slug: props.match.params.boss
        }}
        guild={props.guild}
        fallback={<SkeletonGuildRaidCompositionPage />}
      />
    </div>
  );
}

function EmbeddedGuildDetailsContent(props: any) {
  if (!props.raidProgress) {
    return <Spinner />;
  }

  return (
    <EncounterDetailsContent
      hidePermalink={true}
      withHash={true}
      difficulty={props.match.params.difficulty}
      raid={{
        slug: props.match.params.raid
      }}
      boss={{
        slug: props.match.params.boss
      }}
      guild={props.guild}
    />
  );
}

/**
 * GuildRaidCompositionPage
 */
class GuildRaidCompositionPage extends Component<any, any> {
  public static async serverPreloadActions(params: any) {
    const { match } = params;
    const loadParams: any = {
      region: match.region,
      realm: match.realm,
      guild: decodeURIComponent(match.guild)
    };

    const bossKillParams = {
      ...loadParams,
      raid: match.raid,
      difficulty: match.difficulty,
      boss: match.boss
    };

    const [guildDetails, bossKillDetails] = await Promise.all([
      apiServiceAsync.getGuildDetails(loadParams),
      apiServiceAsync.getGuildBossKillDetails(bossKillParams).catch((err: any) => {
        if (!isExpectedMissingBossDetailsError(err)) {
          throw err;
        }

        frontendLog.log({ err, bossKillParams }, 'getGuildBossKillDetails preload skipped');
        return { killDetails: { ui: bossKillParams } };
      })
    ]);

    const actions: any[] = [createActionGetGuildDetails(guildDetails.guildDetails)];
    if (bossKillDetails) {
      actions.push(
        GuildActionCreator.setGuildBossKillDetails(
          `${match.raid}-${match.boss}-${match.difficulty}`,
          bossKillDetails.killDetails
        )
      );
    }

    return actions;
  }

  public render(): ReactElement<TGuildDetailsProps> {
    if (this.props.siteConfig.isEmbedded) {
      return <EmbeddedGuildDetailsContent {...this.props} />;
    }

    return (
      <div>
        <GuildDetailsContent {...this.props} />
      </div>
    );
  }
}

// take state from store and convert into props
function mapStateToProps(state: any): TPropsFromState {
  return {
    auth: state.auth,
    siteConfig: state.siteConfig,
    ...state.guildDetails,
    isFullProfile:
      state.guildDetails && state.guildDetails.guild ? state.guildDetails.guild.region.slug !== 'cn' : true,
    raids: state.siteMeta!.raids
  };
}

// return props that need access to `dispatch` function (props that can modify the store?)
function mapDispatchToProps(dispatch: Function, ownProps: TOwnProps): TPropsThatDispatch {
  return {
    setGuildDetails(details: TGuildDetailsClientState) {
      dispatch(createActionGetGuildDetails(details));
    },
    setGuildPageLoading(loading: boolean) {
      dispatch(createActionSetGuildPageLoading(loading));
    },
    showError(actionData: TError) {
      dispatch(createActionSetError(actionData));
    }
  };
}

// return one entire set of props
function mergeProps(stateProps: TPropsFromState, dispatchProps: TPropsThatDispatch, ownProps: TOwnProps) {
  const loadParams: TGetGuildDetailsParams = {
    region: ownProps.match.params.region,
    realm: ownProps.match.params.realm,
    guild: decodeURIComponent(ownProps.match.params.guild)
  };

  const guildProps = stateProps.guild
    ? {
        region: stateProps.guild.region.slug,
        realm: stateProps.guild.realm.slug,
        guild: decodeURIComponent(stateProps.guild.name)
      }
    : {};

  const checkFields = ['region', 'realm', 'guild'];
  const guildChanged = !stateProps.guild || areFieldsDifferent(checkFields, loadParams, guildProps);

  if (!stateProps.isLoading && guildChanged) {
    dispatchProps.setGuildPageLoading(true);

    apiService.getGuildDetails(loadParams, (err: any, result?: TGuildDetailsServerState) => {
      if (err) {
        dispatchProps.setGuildPageLoading(false);

        frontendLog.error({ err }, 'getGuildDetails failed');

        if (err.statusCode === 400) {
          history.push('/');
          return;
        }

        dispatchProps.showError({
          title: t('common:errors.something-went-wrong'),
          body: err.toString()
        });

        return;
      }

      if (!result) {
        dispatchProps.setGuildPageLoading(false);

        dispatchProps.showError({
          title: t('common:errors.something-went-wrong'),
          body: t('common:errors.unable-to-load-data-reload')
        });
        frontendLog.error('getGuildDetails empty');
        return;
      }

      dispatchProps.setGuildDetails({ ...result.guildDetails });
      dispatchProps.setGuildPageLoading(false);
    });
  }

  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps
  };
}

export default connect<TPropsFromState, TPropsThatDispatch, TOwnProps>(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(GuildRaidCompositionPage as any);
